/// <amd-module name="Agile/Directory/Redux/DirectorySelectors" />
import { ExceptionInfo } from "Agile/Contracts/Common";
import { IDirectoryRow, IGridRow, IGroupRow, ILoadingRow, IZeroDataRow } from "Agile/Directory/Components/DirectoryGrid";
import * as DirectoryResources from "Agile/Directory/Resources";
import { DirectoryFilterKeys, DirectoryGroupKeys } from "DirectoryConstants";
import {
    FavoriteStatus,
    IArtifactOwner,
    IDirectoryArtifact,
    IDirectoryRowGroup,
    IDirectoryViewState,
    IFavoriteData,
    IFavoriteState
} from "DirectoryContracts";
import * as Reselect from "Reselect";
import { caseInsensitiveContains, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { getCurrentFilterState, isFilterActive } from "VSS/Redux/Deprecated/HubViewState";
import { IFilterState } from "VSSUI/Utilities/Filter";
import { IVssIconProps } from "VSSUI/VssIcon";

/* ---------------
  BASIC SELECTORS
----------------*/

/** Get the current artifacts */
export function getArtifactMap<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): { [key: string]: T } {
    return state.directoryState.directoryArtifactState.artifacts || {};
}

/** Get the deleted artifacts */
export function getDeletedArtifactMap<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): { [key: string]: T } {
    return state.directoryState.directoryArtifactState.deletedArtifacts || {};
}

/** Creates a selector that retrieves an artifact by id */
export function getArtifactById<T extends IDirectoryArtifact>(artifactId: string): (state: IDirectoryViewState<T>) => T | undefined {
    return (state: IDirectoryViewState<T>): T => {
        return state.directoryState.directoryArtifactState.artifacts[artifactId];
    };
}

/** Get the member artifact keys */
export function getMemberKeys<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): string[] {
    return state.directoryState.directoryArtifactState.memberArtifactKeys || [];
}

/** Get the recent artifact keys */
export function getRecentKeys<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): string[] {
    return state.directoryState.directoryArtifactState.recentArtifactKeys || [];
}

/** Get the favorites state */
export function getFavoriteState<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): IFavoriteState {
    return state.directoryState.favoriteState;
}

/** Get the status of a specific favorite */
export function getFavoriteStatus<T extends IDirectoryArtifact>(artifactId: string): (state: IDirectoryViewState<T>) => FavoriteStatus {
    return (state: IDirectoryViewState<T>) => getFavoriteState(state).favoriteStatuses[artifactId];
}

/** Get the status of a specific favorite */
export function getFavoriteStatuses<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): { [key: string]: FavoriteStatus } {
    return getFavoriteState(state).favoriteStatuses;
}

/** Get the grouping collapsed mapping */
export function getGroupCollapsedMap<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): { [key: string]: boolean } {
    return state.directoryState.uiState.rowGroupCollapsedMap;
}

export function isFilterVisible<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): boolean {
    return state.directoryState.uiState.isFilterBarVisible;
}

/**
 * Gets the exception info, if present
 */
export function getExceptionInfo<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): ExceptionInfo | undefined {
    return state.directoryState.uiState.exceptionInfo;
}

/** Is the all section loading? */
export function isAllSectionLoading<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): boolean {
    return state.directoryState.uiState.isAllSectionLoading;
}

/** Has the all section ever been populated? */
export function isAllSectionPopulated<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): boolean {
    return state.directoryState.uiState.isAllSectionPopulated;
}

/** Gets the current favorites */
export function getFavoriteMap<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): { [key: string]: IFavoriteData } {
    return state.directoryState.favoriteState.favorites;
}

/** Get any errors associated with the artifacts */
export function getFavoriteErrors<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>): { [key: string]: Error } {
    return state.directoryState.favoriteState.favoriteErrors;
}

export function getArtifactIcon<T extends IDirectoryArtifact>(
    state: IDirectoryViewState<T>,
    props: { artifactIcon?: IVssIconProps }
): IVssIconProps | undefined {
    return props.artifactIcon;
}

export function getArtifactName<T extends IDirectoryArtifact>(state: IDirectoryViewState<T>, props: { artifactName?: string }): string {
    return props.artifactName || DirectoryResources.DefaultArtifactName;
}

/**
 * Create selectors that should be disposed when a connected component is unmounted
 */
export function createInstanceSelectors() {
    /** Get a list of favorites */
    const getFavoriteList = Reselect.createSelector(
        [getFavoriteState],
        (favoriteState: IFavoriteState) => {
            const favorites = favoriteState.favorites || {};
            const favoriteStatuses = favoriteState.favoriteStatuses || {};
            return Object.keys(favorites)
                .filter(key => favoriteStatuses[key] === FavoriteStatus.Favoriting || favoriteStatuses[key] === FavoriteStatus.Favorited)
                .map(key => favorites[key]);
        }
    );

    /** Type alias for a function that produces a directory row */
    type DirectoryRowBuilder<T extends IDirectoryArtifact> = (artifact: T, groupKey?: string) => IDirectoryRow<T>;

    /*-------------------
      Artifact selectors
    -------------------*/

    /**
     * Get a sorted list of the artifacts that have been favorited
     * Depends on the {@link getFavoriteList} and {@link getArtifactMap} functions
     */
    const getSortedFavoriteArtifacts = Reselect.createSelector(
        [getFavoriteList, getArtifactMap, getDeletedArtifactMap],
        <T extends IDirectoryArtifact>(favorites: IFavoriteData[], artifacts: { [key: string]: T }, deletedArtifacts: { [key: string]: T }) => {
            return favorites
                .map(favorite => (favorite.isDeleted ? deletedArtifacts[favorite.artifactId] : artifacts[favorite.artifactId]))
                .filter(item => item)
                .sort(_artifactComparator);
        }
    );

    /**
     * Get a sorted list of the recent artifacts
     * Depends on the {@link getRecentKeys} and the {@link getArtifactMap} functions
     */
    const getSortedRecentArtifacts = Reselect.createSelector(
        [getRecentKeys, getArtifactMap],
        <T extends IDirectoryArtifact>(recentKeys: string[], artifacts: { [key: string]: T }): T[] => {
            return recentKeys.map(key => artifacts[key]).sort(_artifactComparator);
        }
    );

    /**
     * Get a sorted list of the member artifacts
     * Depends on the {@link getMemberKeys} and the {@link getArtifactMap} functions
     */
    const getSortedMemberArtifacts = Reselect.createSelector(
        [getMemberKeys, getArtifactMap],
        <T extends IDirectoryArtifact>(memberKeys: string[], artifacts: { [key: string]: T }): T[] => {
            return memberKeys.map(key => artifacts[key]).sort(_artifactComparator);
        }
    );

    /**
     * Get a sorted list of all the artifacts
     * Depends on the {@link getArtifactMap} function
     */
    const getSortedArtifacts = Reselect.createSelector(
        [getArtifactMap],
        <T extends IDirectoryArtifact>(artifactMap: { [key: string]: T }): T[] => {
            return Object.keys(artifactMap)
                .map(k => artifactMap[k])
                .sort(_artifactComparator);
        }
    );

    /**
     * Cache of rows that have already been constructed
     */
    const rowCache: { [key: string]: IDirectoryRow<any> } = {};

    /**
     * Selector which returns a "row builder" function. This will composite information from the state with an artifact to build a DirectoryRow
     * for use within the Directory Grid
     */
    const directoryRowBuilder = Reselect.createSelector(
        [getFavoriteMap, getFavoriteStatuses, getFavoriteErrors, getArtifactIcon],
        <T extends IDirectoryArtifact>(
            favorites: { [key: string]: IFavoriteData },
            favoriteStatuses: { [key: string]: FavoriteStatus },
            favoriteErrors: { [key: string]: Error },
            artifactIcon?: IVssIconProps
        ): DirectoryRowBuilder<T> => {
            return (artifact: T, groupKey?: string) => {
                const key = groupKey ? `${groupKey} - ${artifact.id}` : artifact.id;
                const favoriteArtifact: IFavoriteData = favorites[artifact.id];
                const favoriteStatus: FavoriteStatus = favoriteStatuses[artifact.id];
                const isDeleted = favoriteArtifact ? favoriteArtifact.isDeleted : false;
                const isFavorited =
                    !!favoriteArtifact && favoriteStatus !== FavoriteStatus.Unfavorited && favoriteStatus !== FavoriteStatus.Unfavoriting;
                const errorMessage = favoriteErrors[artifact.id] ? favoriteErrors[artifact.id].message : undefined;

                const builtRow = rowCache[key];
                if (
                    !builtRow ||
                    builtRow.isDeleted !== isDeleted ||
                    builtRow.isFavorited !== isFavorited ||
                    builtRow.data !== artifact ||
                    builtRow.errorMessage !== errorMessage ||
                    builtRow.iconProps !== artifactIcon
                ) {
                    rowCache[key] = {
                        key,
                        isDeleted,
                        isFavorited,
                        groupKey: groupKey,
                        data: artifact,
                        errorMessage,
                        iconProps: artifactIcon
                    };
                }

                return rowCache[key];
            };
        }
    );

    /**
     * Given a selector function, construct a group selector which will build a grouping
     * @param groupKey The group key
     * @param groupHeader The header of the group to create
     * @param selector The selector function to use to build the group
     */
    function getGroupSelector<T extends IDirectoryArtifact>(
        groupKey: string,
        groupHeader: string,
        selector: (state: IDirectoryViewState<T>) => T[],
        includeSuffix: boolean = true,
        toggleable: boolean = true,
        alwaysVisible: boolean = false
    ) {
        return Reselect.createSelector(
            [selector, getGroupCollapsedMap, getArtifactName],
            (items: T[], groupCollapsedMap: { [key: string]: boolean }, artifactName: string): IDirectoryRowGroup<T> => {
                return {
                    alwaysVisible,
                    groupKey,
                    groupHeader: includeSuffix ? `${groupHeader} ${artifactName.toLocaleLowerCase()}` : groupHeader,
                    collapsed: !!groupCollapsedMap[groupKey],
                    items,
                    toggleable
                };
            }
        );
    }

    /** Get the recent grouping */
    const getRecentGroup = getGroupSelector(
        DirectoryGroupKeys.RecentGroupKey,
        DirectoryResources.RecentGroupHeading,
        getSortedRecentArtifacts,
        false,
        false
    );
    /** Get the favorite grouping */
    const getFavoriteGroup = getGroupSelector(
        DirectoryGroupKeys.FavoriteGroupKey,
        DirectoryResources.FavoriteGroupHeading,
        getSortedFavoriteArtifacts,
        true,
        true,
        true
    );
    /** Get the member grouping */
    const getMemberGroup = getGroupSelector(DirectoryGroupKeys.MineGroupKey, DirectoryResources.MemberGroupHeading, getSortedMemberArtifacts);
    /** Get the all grouping */
    const getAllGroup = getGroupSelector(DirectoryGroupKeys.AllGroupKey, DirectoryResources.AllGroupHeading, getSortedArtifacts, true, true, true);

    /**
     * Selector which takes the Directory groupings (recent, favorite, member, all) and returns a flat list of rows
     * for use in the Directory Grid component
     *
     * Depends on the {@link getRecentGroup}, {@link getFavoriteGroup}, {@link getMemberGroup}, {@link getAllGroup}, {@link isAllSectionLoading}, and {@link directoryRowBuilder} selectors
     */
    const getGroupedRows = Reselect.createSelector(
        [getRecentGroup, getFavoriteGroup, getMemberGroup, getAllGroup, isAllSectionLoading, directoryRowBuilder],
        <T extends IDirectoryArtifact>(
            recentGroup: IDirectoryRowGroup<T>,
            favoriteGroup: IDirectoryRowGroup<T>,
            memberGroup: IDirectoryRowGroup<T>,
            allGroup: IDirectoryRowGroup<T>,
            isAllSectionLoading: boolean,
            buildDirectoryRow: DirectoryRowBuilder<T>
        ): IGridRow<T>[] => {
            const groups = [recentGroup, favoriteGroup, memberGroup, allGroup];
            const flattenedRows: IGridRow<T>[] = [];

            groups.forEach((group: IDirectoryRowGroup<T>) => {
                // If the group has items, or always show the all group
                if (group.items.length > 0 || (group.alwaysVisible || false)) {
                    const groupRow: IGroupRow = {
                        toggleable: group.toggleable,
                        key: group.groupKey,
                        title: _getGroupTitle(group),
                        collapsed: group.collapsed || false,
                        isGroupRow: true
                    };

                    flattenedRows.push(groupRow);

                    if (!group.collapsed) {
                        // SPECIAL CASE: All Group
                        if (group.groupKey === DirectoryGroupKeys.AllGroupKey && isAllSectionLoading) {
                            const loadingRow: ILoadingRow = {
                                key: `${group.groupKey} - LOADING`,
                                groupKey: group.groupKey,
                                isLoadingRow: true
                            };

                            flattenedRows.push(loadingRow);
                        } else if (group.items.length === 0) {
                            const emptyRow: IZeroDataRow = {
                                key: `${group.groupKey} - EMPTY`,
                                groupKey: group.groupKey,
                                isZeroDataRow: true
                            };

                            flattenedRows.push(emptyRow);
                        } else {
                            group.items.forEach(item => flattenedRows.push(buildDirectoryRow(item, group.groupKey)));
                        }
                    }
                }
            });

            return flattenedRows;
        }
    );

    /**
     * Selector which gets a flat list of filtered rows
     *
     * Depends on the {@link getFilter}, {@link getSortedArtifacts}, and {@link directoryRowBuilder} selectors
     */
    const getFilteredRows = Reselect.createSelector(
        [getCurrentFilterState, getSortedArtifacts, directoryRowBuilder, isAllSectionPopulated],
        <T extends IDirectoryArtifact>(
            filter: IFilterState,
            sortedArtifacts: T[],
            buildDirectoryRow: DirectoryRowBuilder<T>,
            isAllSectionPopulated: boolean
        ): IDirectoryRow<T>[] => {
            return isAllSectionPopulated ? sortedArtifacts.filter(a => _artifactMatchesFilter(filter, a)).map(v => buildDirectoryRow(v)) : [];
        }
    );

    /**
     * Function which either gets the grouped rows, or a flat list of filetered rows, depending on the filter state
     */
    return {
        getDirectoryRows: <T extends IDirectoryArtifact>(
            state: IDirectoryViewState<T>,
            props: { artifactIcon?: IVssIconProps; artifactName?: string }
        ): IGridRow<T>[] => {
            if (!getExceptionInfo(state)) {
                if (isFilterActive(state)) {
                    return getFilteredRows(state, props) as IGridRow<T>[];
                } else {
                    return getGroupedRows(state, props) as IGridRow<T>[];
                }
            }
            return [];
        }
    };
}

export const getAllOwners = Reselect.createSelector(
    [getArtifactMap],
    <T extends IDirectoryArtifact>(artifactMap: { [key: string]: T }) => {
        const ownersMap: { [key: string]: IArtifactOwner } = {};
        for (const artifactId in artifactMap) {
            const artifact = artifactMap[artifactId];
            for (const artifactOwner of artifact.owners) {
                ownersMap[artifactOwner.id] = artifactOwner;
            }
        }

        const ownersList: IArtifactOwner[] = [];
        for (const ownerId in ownersMap) {
            ownersList.push(ownersMap[ownerId]);
        }

        ownersList.sort(_artifactComparator);
        return ownersList;
    }
);

/**
 * Compare two artifacts by name
 */
function _artifactComparator<T extends IDirectoryArtifact | IArtifactOwner>(artifactA: T, artifactB: T): number {
    if (!artifactA && !artifactB) {
        return 0;
    }

    if (!artifactA) {
        return 1;
    }

    if (!artifactB) {
        return -1;
    }

    return localeIgnoreCaseComparer(artifactA.name, artifactB.name);
}

/**
 * Checks if a team matches a filter, by text or team id
 * @param activeFilter The active filter
 * @param team The team
 */
function _artifactMatchesFilter(activeFilter: IFilterState, artifact: IDirectoryArtifact): boolean {
    let matchesFilter = true;
    const keywordFilterItemState = activeFilter[DirectoryFilterKeys.KEYWORD_FILTER_KEY];
    const ownerFilterItemState = activeFilter[DirectoryFilterKeys.TEAM_FILTER_KEY];
    const keywordFilterValue: string = keywordFilterItemState ? keywordFilterItemState.value : undefined;
    const ownerFilterValue: string[] = ownerFilterItemState ? ownerFilterItemState.value.map(v => v.key) : undefined;

    if (matchesFilter && ownerFilterValue && ownerFilterValue.length) {
        let ownerMatchFound = false;
        if (artifact.owners) {
            for (const owner of artifact.owners) {
                const ownerId = owner.id;
                if (ownerFilterValue.find(oId => oId === ownerId)) {
                    ownerMatchFound = true;
                    break;
                }
            }
        }

        matchesFilter = ownerMatchFound;
    }

    if (matchesFilter && keywordFilterValue) {
        matchesFilter = caseInsensitiveContains(artifact.name, keywordFilterValue);
    }

    return matchesFilter;
}

/**
 * Returns the title string to be used for the given group.
 * @param group the group to use
 */
function _getGroupTitle<T extends IDirectoryArtifact>(group: IDirectoryRowGroup<T>): string {
    const isUncountedGroup = group.groupKey === DirectoryGroupKeys.RecentGroupKey || group.groupKey === DirectoryGroupKeys.AllGroupKey;
    if (isUncountedGroup || !group.items || group.items.length <= 0) {
        return group.groupHeader;
    }

    return `${group.groupHeader} (${group.items.length})`;
}
